import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Box, Tabs, Tab, Button, CircularProgress, Typography, ToggleButton, ToggleButtonGroup, Container } from '@mui/material';
import { LayoutDashboard, TableProperties, RefreshCw, Users, FileText } from 'lucide-react';
import QuoteTable from '../components/QuoteTable';
import KanbanBoard from '../components/KanbanBoard';
import LeadKanbanBoard from '../components/LeadKanbanBoard';
import LeadTable from '../components/LeadTable';
import QuoteKPIs from '../components/QuoteKPIs';
import MultiLineMetricsChart from '../components/MultiLineMetricsChart';
import FloatingDialer from '../components/FloatingDialer';
import CompactDateRangePicker from '../components/CompactDateRangePicker';

const HomePage = () => {
    // Get current week's start (Monday) and end (Sunday)
    const getThisWeek = () => {
        const today = new Date();
        const currentDay = today.getDay();
        const monday = new Date(today);
        monday.setDate(today.getDate() - (currentDay === 0 ? 6 : currentDay - 1));
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);

        return {
            start: monday.toISOString().split('T')[0],
            end: sunday.toISOString().split('T')[0]
        };
    };

    const thisWeek = getThisWeek();

    // State management
    const [quotes, setQuotes] = useState([]);
    const [leads, setLeads] = useState([]);
    const [logs, setLogs] = useState([]);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState(thisWeek.start);
    const [endDate, setEndDate] = useState(thisWeek.end);
    const [viewMode, setViewMode] = useState('kanban');
    const [dataSource, setDataSource] = useState('leads'); // 'quotes' or 'leads'
    const [activeFilter, setActiveFilter] = useState(null);

    const handleFilterChange = (filter) => {
        setActiveFilter(filter);
    };

    const handleDateChange = (dateRange) => {
        if (Array.isArray(dateRange) && dateRange.length === 2) {
            const start = dateRange[0].format('YYYY-MM-DD');
            const end = dateRange[1].format('YYYY-MM-DD');
            setStartDate(start);
            setEndDate(end);
        }
    };

    // Function to fetch quotes from the backend
    const fetchQuotes = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/quotes', {
                params: {
                    start_date: startDate,
                    end_date: endDate
                }
            });
            console.log('Fetched quotes data:', response.data);

            if (response.data && response.data.length > 0) {
                setQuotes(response.data);
            } else {
                console.log('No quotes data received, using sample data');
                setQuotes(sampleQuotes);
            }
        } catch (error) {
            console.error('Error fetching quotes:', error);
            console.log('Using sample quotes data due to error');
            setQuotes(sampleQuotes);
        } finally {
            setLoading(false);
        }
    };

    // Function to fetch leads from the backend
    const fetchLeads = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/leads', {
                params: {
                    start: startDate,
                    end: endDate
                }
            });
            console.log('Fetched leads data:', response.data);

            if (response.data && response.data.length > 0) {
                setLeads(response.data);
            } else {
                console.log('No leads data received, using sample data');
                setLeads(sampleLeads);
            }
        } catch (error) {
            console.error('Error fetching leads:', error);
            console.log('Using sample leads data due to error');
            setLeads(sampleLeads);
        } finally {
            setLoading(false);
        }
    };

    // Function to fetch logs
    const fetchLogs = async () => {
        try {
            const response = await axios.get('/api/logs/all', {
                params: {
                    start: startDate,
                    end: endDate
                }
            });
            console.log('Fetched logs data:', response.data);

            if (response.data && response.data.length > 0) {
                setLogs(response.data);
            } else {
                console.log('No logs data received, using sample data');
                setLogs(sampleLogs);
            }
        } catch (error) {
            console.error('Error fetching logs:', error);
            console.log('Using sample logs data due to error');
            setLogs(sampleLogs);
        }
    };

    // Function to fetch analytics data
    const fetchAnalyticsData = async () => {
        try {
            console.log('🚀 HOMEPAGE: Fetching analytics data...');
            const response = await axios.get('/api/retool-leads-analytics-fixed', {
                params: {
                    start: startDate,
                    end: endDate
                }
            });
            console.log('🔥 HOMEPAGE: Fetched analytics data:', response.data);

            if (response.data) {
                setAnalyticsData(response.data);
                console.log('✅ HOMEPAGE: Analytics data set successfully');
            } else {
                console.log('⚠️ HOMEPAGE: No analytics data received');
                setAnalyticsData(null);
            }
        } catch (error) {
            console.error('❌ HOMEPAGE: Error fetching analytics data:', error);
            setAnalyticsData(null);
        }
    };

    // Load data when component mounts or dates change
    useEffect(() => {
        if (dataSource === 'quotes') {
            fetchQuotes();
        } else {
            fetchLeads();
        }
        fetchLogs();
        fetchAnalyticsData();
    }, [startDate, endDate, dataSource]);

    // Handle status changes in Kanban board
    const handleStatusChange = (itemId, newStatus) => {
        console.log(`${dataSource === 'quotes' ? 'Quote' : 'Lead'} ${itemId} status changed to ${newStatus}`);

        if (dataSource === 'quotes') {
            setQuotes(prevQuotes =>
                prevQuotes.map(quote =>
                    quote.id === itemId ? { ...quote, status: newStatus } : quote
                )
            );
        } else {
            setLeads(prevLeads =>
                prevLeads.map(lead =>
                    lead.id === itemId ? { ...lead, status: newStatus } : lead
                )
            );
        }
    };

    // Sample data for fallback
    const sampleQuotes = [
        { id: 'Q001', customer: 'Acme Corp', status: 'Approved', date_issued: '2023-01-01' },
        { id: 'Q002', customer: 'XYZ Inc', status: 'Pending', date_issued: '2023-01-10' },
        { id: 'Q003', customer: 'Global Enterprises', status: 'In Review', date_issued: '2023-01-15' },
        { id: 'Q004', customer: 'Tech Solutions', status: 'Draft', date_issued: '2023-01-20' },
        { id: 'Q005', customer: 'Innovative Labs', status: 'Rejected', date_issued: '2023-01-25' },
    ];

    const sampleLeads = [
        { id: 'L001', customer: 'Potential Client A', status: 'New', date_created: '2023-01-01' },
        { id: 'L002', customer: 'Potential Client B', status: 'Contacted', date_created: '2023-01-02' },
        { id: 'L003', customer: 'Potential Client C', status: 'Qualified', date_created: '2023-01-03' },
        { id: 'L004', customer: 'Potential Client D', status: 'Proposal', date_created: '2023-01-04' },
        { id: 'L005', customer: 'Potential Client E', status: 'Won', date_created: '2023-01-05' },
    ];

    const sampleLogs = [
        { quote_id: 'Q001', message: 'Created quote', date_logged: '2023-01-01', type: 'creation' },
        { quote_id: 'Q001', message: 'Updated pricing', date_logged: '2023-01-02', type: 'update' },
        { quote_id: 'Q001', message: 'Approved by manager', date_logged: '2023-01-03', type: 'status_change' },
    ];

    // Main container to be full width and left-aligned with improved layout
    return (
        <Box sx={{
            width: '100%',
            minHeight: '100vh',
            bgcolor: '#121212',
            color: '#e0e0e0',
            pt: 8,
            overflow: 'visible', // Allow content to expand
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start' // Force left alignment of all child content
        }}>
            {/* Header with controls - switched to full width */}
            <Box sx={{
                width: '100%',
                mb: 3, // Increased margin for better separation
                px: 3
            }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2
                }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        {/* Date Range Picker */}
                        <CompactDateRangePicker
                            startDate={startDate}
                            endDate={endDate}
                            onChange={handleDateChange}
                        />

                        {/* View toggle tabs */}
                        <Tabs
                            value={viewMode}
                            onChange={(e, newValue) => setViewMode(newValue)}
                            aria-label="View mode tabs"
                            indicatorColor="primary"
                            textColor="inherit"
                            sx={{
                                minHeight: 32,
                                '& .MuiTab-root': {
                                    color: '#e0e0e0',
                                    minHeight: 32,
                                    py: 0.5,
                                    '&.Mui-selected': {
                                        color: '#2196f3'
                                    }
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: '#2196f3'
                                }
                            }}
                        >
                            <Tab
                                value="kanban"
                                label="Kanban"
                                icon={<LayoutDashboard size={16} />}
                                iconPosition="start"
                            />
                            <Tab
                                value="table"
                                label="Table"
                                icon={<TableProperties size={16} />}
                                iconPosition="start"
                            />
                        </Tabs>

                        {/* Data source toggle */}
                        <ToggleButtonGroup
                            value={dataSource}
                            exclusive
                            onChange={(e, value) => value && setDataSource(value)}
                            size="small"
                            sx={{
                                '& .MuiToggleButton-root': {
                                    color: '#e0e0e0',
                                    borderColor: 'rgba(255, 255, 255, 0.2)',
                                    '&.Mui-selected': {
                                        bgcolor: '#2196f3',
                                        color: '#ffffff'
                                    }
                                }
                            }}
                        >
                            <ToggleButton value="leads" aria-label="leads">
                                <Users size={16} style={{ marginRight: 4 }} />
                                Leads
                            </ToggleButton>
                            <ToggleButton value="quotes" aria-label="quotes">
                                <FileText size={16} style={{ marginRight: 4 }} />
                                Quotes
                            </ToggleButton>
                        </ToggleButtonGroup>

                        {/* Refresh button */}
                        <Button
                            variant="outlined"
                            startIcon={loading ? <CircularProgress size={16} /> : <RefreshCw size={16} />}
                            onClick={dataSource === 'quotes' ? fetchQuotes : fetchLeads}
                            disabled={loading}
                            sx={{
                                borderColor: 'rgba(255, 255, 255, 0.2)',
                                color: '#e0e0e0',
                                '&:hover': {
                                    borderColor: '#2196f3',
                                    bgcolor: 'rgba(33, 150, 243, 0.1)'
                                }
                            }}
                        >
                            Refresh
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* KPIs Section with filtering - switched to full width */}
            <Box sx={{ width: '100%', mb: 4, px: 3 }}>  {/* Increased margin for better separation */}
                <QuoteKPIs
                    quotes={dataSource === 'quotes' ? quotes : []}
                    leads={dataSource === 'leads' ? leads : []}
                    logs={logs}
                    isLeadMode={dataSource === 'leads'}
                    dateRange={{ start: startDate, end: endDate }}
                    onFilterChange={handleFilterChange}
                    dataSource={dataSource}
                />
            </Box>

            {/* Metrics Chart - Fixed height and contained - switched to full width */}
            <Box sx={{ width: '100%', mb: 4, px: 3, pr: 5 }}>  {/* Added pr: 5 for extra right padding */}
                <Box sx={{
                    height: 'auto',
                    minHeight: 100,
                    width: '100%',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 1,
                    bgcolor: '#1a1a1a',
                    p: 2, // Reduced padding
                    pb: 3, // Reduced bottom padding
                    pr: 4, // Extra right padding for better spacing
                    overflow: 'hidden', // Change back to hidden to contain the chart
                    position: 'relative', // Added for proper positioning
                    zIndex: 1 // Ensure proper stacking
                }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#ffffff' }}> {/* Reduced margin */}
                        Metrics Timeline
                    </Typography>
                    <Box sx={{
                        //height: 250, // Slightly reduced fixed height for better separation
                        width: '100%',
                        position: 'relative'
                    }}>
                        <MultiLineMetricsChart
                            leadData={dataSource === 'leads' ? leads : quotes}
                            pageViewData={dataSource === 'leads' ? leads.flatMap(lead => {
                                // Debug the page views for this lead
                                console.log(`Processing page views for lead ${lead.id}:`, lead.page_views);

                                // Use an explicitly formatted page view array
                                let formattedPageViews = [];

                                try {
                                    // Handle different formats of page_views
                                    if (!lead.page_views) {
                                        console.log(`No page_views for lead ${lead.id}`);
                                        return [];
                                    }

                                    // Handle string format (JSON)
                                    if (typeof lead.page_views === 'string') {
                                        try {
                                            formattedPageViews = JSON.parse(lead.page_views);
                                            console.log(`Parsed page_views string for lead ${lead.id}:`, formattedPageViews);
                                        } catch (e) {
                                            console.error(`Error parsing page_views string for lead ${lead.id}:`, e);

                                            // Try alternative parsing if the string might contain single quotes instead of double quotes
                                            try {
                                                // Replace single quotes with double quotes for JSON compatibility
                                                const fixedJsonStr = lead.page_views
                                                    .replace(/'/g, '"')
                                                    .replace(/([{,])\s*(\w+):/g, '$1"$2":'); // Fix unquoted property names

                                                formattedPageViews = JSON.parse(fixedJsonStr);
                                                console.log(`Parsed fixed page_views string for lead ${lead.id}:`, formattedPageViews);
                                            } catch (innerError) {
                                                console.error(`Failed alternative parsing for lead ${lead.id}:`, innerError);
                                                return [];
                                            }
                                        }
                                    }
                                    // Handle array format (direct)
                                    else if (Array.isArray(lead.page_views)) {
                                        formattedPageViews = lead.page_views;
                                        console.log(`Using array page_views for lead ${lead.id}, length:`, formattedPageViews.length);
                                    }
                                    // Handle PostgreSQL JSONB format (might be an object with nested data)
                                    else if (typeof lead.page_views === 'object' && lead.page_views !== null) {
                                        // If it's a page view object itself, wrap it in an array
                                        if (lead.page_views.created_at || lead.page_views.datetime) {
                                            formattedPageViews = [lead.page_views];
                                            console.log(`Wrapped single object page_view for lead ${lead.id}`);
                                        }
                                        // If it contains an array under a different property
                                        else if (lead.page_views.data && Array.isArray(lead.page_views.data)) {
                                            formattedPageViews = lead.page_views.data;
                                            console.log(`Extracted page_views.data array for lead ${lead.id}, length:`, formattedPageViews.length);
                                        }
                                        // Otherwise, try to identify array properties
                                        else {
                                            for (const key in lead.page_views) {
                                                if (Array.isArray(lead.page_views[key])) {
                                                    formattedPageViews = lead.page_views[key];
                                                    console.log(`Found array in page_views.${key} for lead ${lead.id}, length:`, formattedPageViews.length);
                                                    break;
                                                }
                                            }
                                        }
                                    }
                                    // Handle null or other unexpected formats
                                    else {
                                        console.warn(`Unexpected page_views format for lead ${lead.id}:`, typeof lead.page_views);
                                        return [];
                                    }

                                    // Ensure formattedPageViews is an array
                                    if (!Array.isArray(formattedPageViews)) {
                                        console.warn(`formattedPageViews is not an array for lead ${lead.id}:`, formattedPageViews);
                                        formattedPageViews = [];
                                    }

                                    // Filter out any null/undefined views
                                    formattedPageViews = formattedPageViews.filter(view => view);

                                    // Map and normalize the data structure
                                    const normalizedViews = formattedPageViews.map(view => {
                                        // Use any of the possible date fields
                                        const dateField = view.created_at || view.datetime || view.date || view.timestamp;

                                        // Make sure each view has a created_at field
                                        return {
                                            id: view.id || Math.random().toString(36).substring(7),
                                            created_at: dateField || new Date().toISOString(),
                                            browser: view.browser || view.device || view.user_agent || 'Unknown',
                                            ip_address: view.ip_address || view.ip || '0.0.0.0',
                                            user_agent: view.user_agent || view.device || view.browser || 'Unknown'
                                        };
                                    });

                                    console.log(`Returning ${normalizedViews.length} normalized views for lead ${lead.id}`);
                                    return normalizedViews;
                                } catch (e) {
                                    console.error(`Error processing page views for lead ${lead.id}:`, e, lead);
                                    return [];
                                }
                            }) : []}
                            analyticsData={analyticsData}
                            dateRange={{ start: startDate, end: endDate }}
                            loading={loading}
                        />
                    </Box>
                </Box>
            </Box>

            {/* Main content area - switched to full width with better spacing */}
            <Box sx={{
                flex: 'none', // Don't flex, let content determine height
                minHeight: 'auto', // Remove minimum height constraint
                overflow: 'visible',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                px: 3,
                mt: 2, // Add top margin for separation
                mb: 3 // Add bottom margin
            }}>
                {/* Optional search bar for kanban/table if needed */}
                {/* <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search..."
                        size="small"
                        sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: 1,
                            input: { color: '#e0e0e0' },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.1)',
                            },
                        }
                    }}
                />
            </Box> */}

                {viewMode === 'kanban' ? (
                    <Box sx={{
                        overflow: 'visible',
                        width: '100%',
                        maxWidth: '100%',
                        height: 'auto',
                        mt: 2 // Add top margin for separation
                    }}>
                        {dataSource === 'quotes' ? (
                            <KanbanBoard
                                quotes={quotes}
                                logs={logs}
                                onStatusChange={handleStatusChange}
                                activeFilter={activeFilter}
                            />
                        ) : (
                            <LeadKanbanBoard
                                leads={leads}
                                logs={logs}
                                onStatusChange={handleStatusChange}
                                activeFilter={activeFilter}
                            />
                        )}
                    </Box>
                ) : (
                    <Box sx={{
                        flex: 1,
                        overflow: 'auto',
                        borderRadius: 1,
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        bgcolor: '#1a1a1a',
                        width: '100%',
                        maxWidth: '100%',
                        mt: 2 // Add top margin for separation
                    }}>
                        {dataSource === 'quotes' ? (
                            <QuoteTable
                                quotes={quotes}
                                logs={logs}
                                onFilterChange={handleFilterChange}
                                activeFilter={activeFilter}
                            />
                        ) : (
                            <LeadTable
                                leads={leads}
                                logs={logs}
                                onFilterChange={handleFilterChange}
                                activeFilter={activeFilter}
                            />
                        )}
                    </Box>
                )}
            </Box>

            {/* Floating Dialer */}
            <FloatingDialer />
        </Box>
    );
};

export default HomePage;
